/**
 * Toast Utility
 *
 * Simple event-based toast system without React Context.
 * Components subscribe to toast events.
 */

import type { Toast, ToastType } from "@/ui/primitives/Toast";

type ToastListener = (
  message: string,
  type: ToastType,
  duration?: number,
) => void;

const toastListeners = new Set<ToastListener>();

/**
 * Toast API - call these functions to show toasts
 */
export const toast = {
  success: (message: string, duration?: number) => {
    toastListeners.forEach((listener) =>
      listener(message, "success", duration),
    );
  },

  error: (message: string, duration?: number) => {
    toastListeners.forEach((listener) => listener(message, "error", duration));
  },

  info: (message: string, duration?: number) => {
    toastListeners.forEach((listener) => listener(message, "info", duration));
  },

  warning: (message: string, duration?: number) => {
    toastListeners.forEach((listener) =>
      listener(message, "warning", duration),
    );
  },
};

/**
 * Subscribe to toast events
 * Returns unsubscribe function
 */
export function subscribeToToast(listener: ToastListener): () => void {
  toastListeners.add(listener);
  return () => {
    toastListeners.delete(listener);
  };
}
